/**
 * VoyageCard — tarjeta agrupadora por salida (zarpe) para la sub-página "Hoy".
 * Header con hora (time-block estilo horario portuario), paquete + lancha, y
 * medidor de cupos. Body con las reservas en dos modos: Tarjetas (default) o
 * Tabla. La preferencia se decide arriba, en HoyPage.
 *
 * Expandido por default (decisión del operador: siempre ve todo al abrir el
 * día). Se puede colapsar con click en el header.
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Chip, IconButton, Collapse, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import EstadoChip from './EstadoChip';
import EstadoEmbarqueChip from './EstadoEmbarqueChip';
import SaldoDestinoChip from './SaldoDestinoChip';
import VoucherSubsanarChip from './VoucherSubsanarChip';
import { formatPrecio, formatHora, fmtPax, puedeBorrarReserva } from './format';
import { formatNombre } from '../../../utils/formatNombre';
import EvidenciaModal from '../EvidenciaModal';

// Config visual del chip "Observación IA" según el estado del OCR.
// Reusa los mismos labels/colores que ReservacionesTable para consistencia.
const OCR_CHIP = {
  ok:          { bg: '#dcfce7', color: '#166534', label: '✓ Titular OK' },
  no_coincide: { bg: '#fef3c7', color: '#a16207', label: '⚠ No coincide' },
  no_leible:   { bg: '#fee2e2', color: '#991b1b', label: '✗ No legible' },
  omitido:     { bg: '#fef3c7', color: '#a16207', label: '! Omitido' },
  sin_ocr:     { bg: '#e0f2fe', color: '#0369a1', label: 'Sin OCR' },
  sin_foto:    { bg: '#f1f5f9', color: '#94a3b8', label: '—' },
};

/**
 * CheckinChips — chip 📷 (clickable → modal) + chip observación IA.
 * Se muestra en ambas vistas (tarjeta y tabla) sobre cada reserva.
 */
function CheckinChips({ r, onOpenEvidencia, compact = false }) {
  const ev = r.checkin_evidencia || { total: 0, con_foto: 0, titular_con_foto: false };
  const con = ev.con_foto || 0;
  const estado = ev.estado_ocr || (con > 0 ? 'sin_ocr' : 'sin_foto');
  const obs = ev.observacion_ia || '';
  const ocr = OCR_CHIP[estado] || OCR_CHIP.sin_foto;

  const clickable = con > 0 && typeof onOpenEvidencia === 'function';
  const camaraColor = con === 0 ? '#cbd5e1' : (ev.titular_con_foto ? '#10b981' : '#f59e0b');
  const camaraLabel = con === 0
    ? 'Sin evidencia'
    : ev.titular_con_foto
      ? (con > 1 ? `Titular + ${con - 1} acomp.` : 'Titular')
      : 'Solo acompañantes';

  const IconEl = con === 0
    ? <PhotoCameraOutlinedIcon sx={{ fontSize: compact ? 14 : 16, color: camaraColor }} />
    : <PhotoCameraIcon sx={{
        fontSize: compact ? 14 : 16, color: camaraColor,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { transform: 'scale(1.15)' } : undefined,
        transition: 'transform 0.12s',
      }} onClick={clickable ? (e) => { e.stopPropagation(); onOpenEvidencia(r); } : undefined} />;

  const obsChip = (
    <Chip size="small" label={ocr.label}
      sx={{
        bgcolor: ocr.bg, color: ocr.color, fontWeight: 700,
        fontSize: compact ? 9 : 10, height: compact ? 16 : 18,
        '& .MuiChip-label': { px: compact ? 0.5 : 0.75 },
      }} />
  );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
      onClick={clickable ? (e) => e.stopPropagation() : undefined}>
      {clickable ? <Tooltip title={camaraLabel}>{IconEl}</Tooltip> : (
        <Tooltip title={camaraLabel}><span>{IconEl}</span></Tooltip>
      )}
      {obs ? <Tooltip title={obs} arrow>{obsChip}</Tooltip> : obsChip}
    </Box>
  );
}

const ESTADO_COLOR = {
  pendiente: '#f59e0b', confirmada: '#3b82f6', hold: '#a855f7',
  completada: '#10b981', cancelada: '#ef4444', no_show: '#7f1d1d',
  suspendida: '#eab308',
};

function TimeBlock({ hora }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      px: 1.2, py: 0.75, bgcolor: '#0f172a', color: '#f8fafc',
      borderRadius: 1, minWidth: 52, lineHeight: 1,
    }}>
      <Typography sx={{
        fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      }}>
        {formatHora(hora) || '—'}
      </Typography>
      <Typography sx={{
        fontSize: 8.5, fontWeight: 700, mt: 0.25,
        textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.55,
      }}>
        Zarpe
      </Typography>
    </Box>
  );
}

function ReservaCard({ r, onClick, onOpenEvidencia }) {
  const esCortesia = (Number(r.cortesias) || 0) > 0 || r.forma_pago_agencia === 'cortesia';
  const saldo = Number(r.saldo_pendiente || 0);
  const lanchas = (r.salida_embarcaciones || []).filter(Boolean).join(' · ');
  return (
    <Box onClick={onClick} sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '4px 12px',
      p: 1.25,
      bgcolor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 1.5,
      alignItems: 'center',
      cursor: 'pointer',
      transition: 'all 0.12s ease',
      '&:hover': {
        bgcolor: '#fff',
        borderColor: '#cbd5e1',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 6px rgba(15,23,42,0.05)',
      },
    }}>
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 3, height: 22, borderRadius: 1,
                    bgcolor: ESTADO_COLOR[r.estado] || '#cbd5e1', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }} noWrap>
              {formatNombre(r.nombre_cliente) || '—'}
            </Typography>
            {esCortesia && (
              <Chip size="small" label="🎁 Cortesía" sx={{
                bgcolor: '#fef3c7', color: '#92400e',
                fontWeight: 700, fontSize: 10, height: 18, flexShrink: 0,
                '& .MuiChip-label': { px: 0.75 },
              }} />
            )}
          </Box>
          <Typography sx={{
            fontSize: 10.5, color: '#94a3b8',
            fontFamily: 'ui-monospace, monospace', letterSpacing: '0.02em',
          }} noWrap>
            {r.codigo_reserva}{r.numero_whatsapp ? ` · ${r.numero_whatsapp}` : ''}
          </Typography>
        </Box>
      </Box>
      {/* Columna derecha: monto arriba + chips checkin/IA debajo (compactos). */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.35 }}>
        <Typography sx={{
          fontSize: 13, fontWeight: 800, color: '#0f172a', textAlign: 'right',
          fontFamily: 'ui-monospace, monospace', fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
        }}>
          {formatPrecio(r.precio_total, r.moneda)}
        </Typography>
        <CheckinChips r={r} onOpenEvidencia={onOpenEvidencia} compact />
      </Box>
      <Box sx={{
        gridColumn: '1 / -1',
        display: 'flex', flexWrap: 'wrap', gap: 1.2,
        alignItems: 'center',
        fontSize: 11.5, color: '#475569',
      }}>
        <span>{fmtPax(r)}</span>
        {r.agencia_nombre ? (
          <>
            <span style={{ color: '#cbd5e1' }}>·</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              color: '#0f172a', fontWeight: 500,
            }}>
              <Box component="span" sx={{
                width: 6, height: 6, borderRadius: '50%', bgcolor: '#0891b2',
              }} />
              {r.agencia_nombre}
            </span>
          </>
        ) : (
          <>
            <span style={{ color: '#cbd5e1' }}>·</span>
            <span>{r.canal_reserva || 'Directa'}</span>
          </>
        )}
        {lanchas && (
          <>
            <span style={{ color: '#cbd5e1' }}>·</span>
            <span>🚤 {lanchas}</span>
          </>
        )}
      </Box>
      <Box sx={{ gridColumn: '2', justifySelf: 'end', mt: 0.25 }}>
        {saldo > 0 && (r.estado === 'confirmada' || r.estado === 'hold') ? (
          <Chip size="small" label={`Por cobrar ${formatPrecio(saldo)}`}
            sx={{
              bgcolor: '#fef3c7', color: '#b45309',
              fontWeight: 700, fontSize: 10.5, height: 20,
              '& .MuiChip-label': { px: 0.85 },
            }} />
        ) : (
          <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
        )}
        {['embarcado', 'no_show'].includes(r.estado_embarque) && (
          <EstadoEmbarqueChip estadoEmbarque={r.estado_embarque} />
        )}
        <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                          saldoACargoDe={r.saldo_a_cargo_de}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                          formaPagoAgencia={r.forma_pago_agencia} />
        <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
      </Box>
    </Box>
  );
}

function VoyageTable({ rows, onRowClick, onDelete = null, horaCutoff = '09:00', onOpenEvidencia = null }) {
  // Diseño responsive:
  //   · xs (< 600): compact — se ocultan Código (embebido bajo el nombre) y
  //     Agencia (chip pegado al cliente); Total y Estado quedan; tabla NO
  //     obliga scroll horizontal.
  //   · sm+: layout original con todas las columnas.
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <Box sx={{
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 6 },
      '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
    }}>
      <Box component="table" sx={{
        width: '100%',
        minWidth: { xs: 'auto', sm: 640 },
        borderCollapse: 'collapse',
        fontSize: { xs: 12, sm: 12.5 },
        '& thead th': {
          textAlign: 'left', px: { xs: 0.75, sm: 1.5 }, py: 1, bgcolor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          fontSize: { xs: 10, sm: 10.5 }, color: '#475569',
          textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700,
          whiteSpace: 'nowrap',
        },
        '& tbody td': {
          px: { xs: 0.75, sm: 1.5 }, py: { xs: 1, sm: 1.25 },
          borderBottom: '1px solid #f1f5f9', color: '#0f172a',
        },
        '& tbody tr': { cursor: 'pointer' },
        '& tbody tr:hover': { bgcolor: '#f8fafc' },
      }}>
        <thead>
          <tr>
            {isSmUp && <th>Código</th>}
            <th>Cliente</th>
            <th>Pax</th>
            {isSmUp && <th>Agencia</th>}
            <th style={{ textAlign: 'right' }}>Total</th>
            <th>Estado</th>
            {isSmUp && <th style={{ textAlign: 'center' }}>Checkin</th>}
            {isSmUp && <th style={{ textAlign: 'center' }}>Observ. IA</th>}
            {onDelete && <th style={{ textAlign: 'center' }}>{isSmUp ? 'Acciones' : ''}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const del = onDelete ? puedeBorrarReserva(r.salida_fecha, horaCutoff, r.estado_embarque) : null;
            return (
              <tr key={r.id} onClick={() => onRowClick?.(r)}>
                {isSmUp && (
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>
                    {r.codigo_reserva}
                  </td>
                )}
                <td style={{ fontWeight: 600, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box component="span" sx={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {formatNombre(r.nombre_cliente)}
                    </Box>
                    {!isSmUp && (
                      <Box component="span" sx={{
                        fontSize: 10, color: '#94a3b8', fontFamily: 'ui-monospace, monospace',
                        fontWeight: 400, mt: 0.15, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {r.codigo_reserva}
                        {r.agencia_nombre ? ` · ${r.agencia_nombre}` : ''}
                      </Box>
                    )}
                  </Box>
                </td>
                <td>{fmtPax(r)}</td>
                {isSmUp && (
                  <td>{r.agencia_nombre || <span style={{ color: '#94a3b8' }}>directa</span>}</td>
                )}
                <td style={{
                  textAlign: 'right', fontFamily: 'ui-monospace, monospace',
                  fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                }}>
                  {formatPrecio(r.precio_total, r.moneda)}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap',
                                alignItems: 'center' }}>
                    <EstadoChip estado={r.estado} saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto} />
                    {/* El embarque es otra dimensión: una reserva puede seguir
                        «confirmada» en lo administrativo y ya haber viajado.
                        Solo se pinta cuando pasó algo —embarcado o no-show—,
                        para no llenar la tabla de «pendiente». */}
                    {['embarcado', 'no_show'].includes(r.estado_embarque) && (
                      <EstadoEmbarqueChip estadoEmbarque={r.estado_embarque} />
                    )}
                    <SaldoDestinoChip saldoPendiente={r.saldo_pendiente}
                          saldoMuelleMonto={r.saldo_muelle_monto}
                          cxcAgenciaMonto={r.cxc_agencia_monto}
                                      saldoACargoDe={r.saldo_a_cargo_de}
                                      saldoMuelleMonto={r.saldo_muelle_monto}
                                      cxcAgenciaMonto={r.cxc_agencia_monto}
                                      formaPagoAgencia={r.forma_pago_agencia} />
                    <VoucherSubsanarChip voucherPendienteSubsanar={r.voucher_pendiente_subsanar} />
                  </div>
                </td>
                {isSmUp && (
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const ev = r.checkin_evidencia || {};
                      const con = ev.con_foto || 0;
                      const color = con === 0 ? '#cbd5e1' : (ev.titular_con_foto ? '#10b981' : '#f59e0b');
                      const label = con === 0
                        ? 'Sin evidencia'
                        : ev.titular_con_foto
                          ? (con > 1 ? `Titular + ${con - 1} acomp.` : 'Titular')
                          : 'Solo acompañantes';
                      const clickable = con > 0 && typeof onOpenEvidencia === 'function';
                      const Icon = con === 0 ? PhotoCameraOutlinedIcon : PhotoCameraIcon;
                      const iconEl = <Icon sx={{
                        fontSize: 18, color,
                        cursor: clickable ? 'pointer' : 'default',
                        '&:hover': clickable ? { transform: 'scale(1.15)' } : undefined,
                        transition: 'transform 0.12s',
                      }} onClick={clickable ? () => onOpenEvidencia(r) : undefined} />;
                      return <Tooltip title={label}><span>{iconEl}</span></Tooltip>;
                    })()}
                  </td>
                )}
                {isSmUp && (
                  <td style={{ textAlign: 'center' }}>
                    {(() => {
                      const ev = r.checkin_evidencia || {};
                      const estado = ev.estado_ocr || ((ev.con_foto || 0) > 0 ? 'sin_ocr' : 'sin_foto');
                      const c = OCR_CHIP[estado] || OCR_CHIP.sin_foto;
                      const chip = (
                        <Chip label={c.label} size="small"
                          sx={{ bgcolor: c.bg, color: c.color, fontWeight: 700, fontSize: 10, height: 18,
                                '& .MuiChip-label': { px: 0.75 } }} />
                      );
                      return ev.observacion_ia
                        ? <Tooltip title={ev.observacion_ia} arrow>{chip}</Tooltip>
                        : chip;
                    })()}
                  </td>
                )}
                {onDelete && (
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={del.ok ? 'Borrar reserva' : (del.motivo || 'Borrado no permitido')} arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!del.ok}
                          onClick={(e) => { e.stopPropagation(); if (del.ok) onDelete(r); }}
                          sx={{
                            color: del.ok ? '#dc2626' : '#cbd5e1',
                            '&:hover': del.ok ? { bgcolor: '#fef2f2', color: '#991b1b' } : {},
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
}

export default function VoyageCard({
  hora, paquete, lanchas = [], capacidad = 0, ocupados = 0,
  reservas = [], view = 'cards', onRowClick,
  // Cuando el padre pasa onDelete se muestra la columna "Acciones" con el
  // botón de borrar en la vista tabla. En vista cards no aparece por ahora.
  onDelete = null, horaCutoff = '09:00',
}) {
  const [collapsed, setCollapsed] = useState(false);
  // Modal exclusivo de evidencia — se abre al hacer clic en la 📷 de una
  // reserva (tabla o tarjeta) sin abrir el detalle completo.
  const [evidenciaReserva, setEvidenciaReserva] = useState(null);
  const abrirEvidencia = (r) => setEvidenciaReserva(r);

  const { fillPct, sobrecupo } = useMemo(() => {
    if (!capacidad) return { fillPct: 0, sobrecupo: false };
    return {
      fillPct: Math.min(100, (ocupados / capacidad) * 100),
      sobrecupo: ocupados > capacidad,
    };
  }, [ocupados, capacidad]);

  return (
    <Box sx={{
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: sobrecupo ? '3px solid #d97706' : '1px solid #e2e8f0',
      borderRadius: 2,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
    }}>
      <Box onClick={() => setCollapsed(v => !v)} sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'auto 1fr auto auto' },
        alignItems: 'center', gap: { xs: 1, sm: 2 },
        p: { xs: 1.25, sm: 1.75 },
        cursor: 'pointer', userSelect: 'none',
        bgcolor: 'linear-gradient(to bottom, #fff, #f8fafc)',
      }}>
        <TimeBlock hora={hora} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontSize: { xs: 13.5, sm: 14.5 }, fontWeight: 800, color: '#0f172a',
            letterSpacing: '-0.005em',
          }} noWrap>
            {paquete || 'Salida'}
          </Typography>
          <Box sx={{
            display: 'flex', gap: 1, flexWrap: 'wrap',
            fontSize: 11.5, color: '#475569', mt: 0.25,
            alignItems: 'center',
          }}>
            {lanchas.length > 0 && (
              <span>🚤 {lanchas.join(' · ')}</span>
            )}
            {sobrecupo && (
              <>
                <span style={{ color: '#cbd5e1' }}>·</span>
                <span style={{ color: '#b45309', fontWeight: 700 }}>Sobrecupo</span>
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.6 }}>
          <Typography sx={{
            fontFamily: 'ui-monospace, monospace', fontVariantNumeric: 'tabular-nums',
            fontSize: 13, fontWeight: 800, color: '#0f172a',
          }}>
            {ocupados}
            <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}>
              /{capacidad || '—'}
            </Box>
          </Typography>
          {capacidad > 0 && (
            <Box sx={{
              width: { xs: 60, sm: 88 }, height: 5,
              bgcolor: '#e2e8f0', borderRadius: 999, overflow: 'hidden',
            }}>
              <Box sx={{
                width: `${fillPct}%`, height: '100%',
                bgcolor: sobrecupo ? '#d97706' : '#0891b2',
                borderRadius: 999,
              }} />
            </Box>
          )}
        </Box>
        <IconButton size="small" sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          transition: 'transform 0.18s',
          transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
        }}>
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={!collapsed} timeout={180}>
        <Box sx={{
          p: view === 'table' ? 0 : { xs: 1, sm: 1.25 },
          pt: view === 'table' ? 0 : 0.5,
          ...(view === 'cards' && {
            display: 'grid', gap: 1,
            // xs: 1 columna (móvil).
            // sm: 2 columnas si caben (tablet vertical).
            // md+: auto-fill flexible desde 280px (baja de 320 para adaptarse
            //   mejor a 1366×768 con la sidebar de NARA que come ~240px).
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(280px, 1fr))',
              md: 'repeat(auto-fill, minmax(300px, 1fr))',
            },
          }),
        }}>
          {view === 'cards' ? (
            reservas.map(r => (
              <ReservaCard key={r.id} r={r} onClick={() => onRowClick?.(r)}
                onOpenEvidencia={abrirEvidencia} />
            ))
          ) : (
            <VoyageTable
              rows={reservas}
              onRowClick={onRowClick}
              onDelete={onDelete}
              horaCutoff={horaCutoff}
              onOpenEvidencia={abrirEvidencia}
            />
          )}
        </Box>
      </Collapse>

      {/* Modal exclusivo de evidencia — abre al clic en la 📷 de tabla o tarjeta */}
      {evidenciaReserva && (
        <EvidenciaModal
          reservaId={evidenciaReserva.id}
          codigoReserva={evidenciaReserva.codigo_reserva}
          nombreCliente={evidenciaReserva.nombre_cliente}
          onClose={() => setEvidenciaReserva(null)}
        />
      )}
    </Box>
  );
}
